"use client";

import * as React from "react";
import { Lock, AlertCircle } from "lucide-react";
import { StatusBadge } from "../../../feedback/status-badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../primitives/tooltip";
import { cn } from "../../../utils";
import { useLazyOverlay } from "./use-lazy-overlay";

export type CellStatusBadgeKind = "locked" | "needs-review";

export interface CellStatusBadgeProps {
  kind: CellStatusBadgeKind;
  className?: string;
}

const KIND_TO_VARIANT = {
  locked: "locked",
  "needs-review": "needs-review",
} as const;

const KIND_TO_LABEL = {
  locked: "Locked",
  "needs-review": "Needs review",
} as const;

const KIND_TO_ICON = {
  locked: Lock,
  "needs-review": AlertCircle,
} as const;

/**
 * Cell override / state badge. Backed by the WS-0 status-badge variants:
 * `locked`, `needs-review`. Never authors a new variant.
 *
 * The label collapses to icon-only when the enclosing cell container is
 * narrow (container query), with the label shown via tooltip on hover.
 */
export function CellStatusBadge({ kind, className }: CellStatusBadgeProps) {
  const Icon = KIND_TO_ICON[kind];
  const label = KIND_TO_LABEL[kind];

  const badge = (
    <StatusBadge variant={KIND_TO_VARIANT[kind]} icon={Icon} className={cn(className)}>
      <span className="hidden @[14rem]:inline">{label}</span>
    </StatusBadge>
  );

  const { primed, triggerProps } = useLazyOverlay();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span aria-label={label} {...triggerProps}>
          {badge}
        </span>
      </TooltipTrigger>
      {primed && <TooltipContent>{label}</TooltipContent>}
    </Tooltip>
  );
}
